import { useEffect, useState } from "react";
import {
  createCompany,
  getAllCompany,
  getCompanyById,
  updateCompany,
} from "../../../services/admin/manageCompanyServices";
import { showErrorToast, showSuccessToast } from "../../../utils/toast-utils";
import Loader from "../../../components/loader/loader";
import { Button } from "../../../components/ui/button";
import {
  Building,
  Building2,
  FileEdit,
  FilePlus,
  FilePlus2,
  Trash2,
} from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import CompanyDetails from "./components/CompanyDetails";

const initialForm = {
  id: "",
  company_name: "",
  company_logo: null,
  company_qr: null,
  company_email: "",
  company_contact: "",
  company_whatsapp_no: "",
  company_cin: "",
  company_url: "",
  support_url: "",
  director_signature: null,
  favicon: null,
  copyright_name: "",
  facebook_url: "",
  twitter_url: "",
  instagram_url: "",
  youtube_url: "",
  company_capital: "",
  corporate_office_address: "",
  head_office_address: "",
  patient_app: "",
  employee_app: "",
};

const ManageCompany = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [companies, setCompanies] = useState([]);
  const [page, setPage] = useState(1); // for future pagination
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const [form, setForm] = useState(initialForm);
  const [companyIndex, setCompanyIndex] = useState(0);

  const [imageUrls, setImageUrls] = useState({});

  const openCreateModal = () => {
    setImageUrls({})
    setIsEdit(false);
    setForm(initialForm);
    setIsModalOpen(true);
  };

  const openEditModal = async (id) => {
    try {
      setIsLoading(true);
      const res = await getCompanyById(id);
      if (res?.success && res.data) {
        const c = res.data?.data || res.data; // depending on your service shape

        // map API response (camelCase) to form state (snake_case)
        setForm({
          id: c.id,
          company_logo: null, // keep c.; show existing URL separately
          company_qr: null,
          company_email: c.companyEmail || "",
          company_contact: c.companyContact || "",
          company_whatsapp_no: c.companyWhatsappNo || "",
          company_cin: c.companyCin || "",
          company_url: c.companyUrl || "",
          support_url: c.supportUrl || "",
          director_signature: null,
          favicon: null,
          copyright_name: c.copyrightName || "",
          facebook_url: c.facebookUrl || "",
          twitter_url: c.twitterUrl || "",
          instagram_url: c.instagramUrl || "",
          youtube_url: c.youtubeUrl || "",
          company_capital: c.companyCapital || "",
          corporate_office_address: c.corporateOfficeAddress || "",
          head_office_address: c.headOfficeAddress || "",
          patient_app: c.patientApp || "",
          employee_app: c.employeeApp || "",
        });

        setImageUrls({
          company_logo: c.companyLogo,
          company_qr: c.companyQr,
          director_signature: c.directorSignature,
          favicon: c.favicon,
        });
        setIsEdit(true);
        setIsModalOpen(true);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const fetchCompanies = async () => {
    try {
      setIsLoading(true);
      const res = await getAllCompany({ page });
      if (res?.success) {
        // assuming res.data.data is array from your example
        const list = res.data?.data || res.data;
        setCompanies(Array.isArray(list) ? list : []);
      }
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchCompanies();
  }, [page]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleFileChange = (e) => {
    const { name, files } = e.target;
    setForm((prev) => ({ ...prev, [name]: files?.[0] || null }));
  };
  const removeEmptyFields = (obj) => {
    return Object.fromEntries(
      Object.entries(obj).filter(([_, value]) => {
        if (value === null) return false;
        if (value === undefined) return false;
        if (typeof value === "string" && value.trim() === "") return false;
        return true;
      }),
    );
  };
  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      let payload = { ...form };

      // Remove id for create
      if (!isEdit) {
        delete payload.id;
      }

      payload = removeEmptyFields(payload);

      let res;

      if (isEdit) {
        res = await updateCompany(payload);
      } else {
        res = await createCompany(payload);
      }

      if (res?.success) {
        showSuccessToast(
          res?.data?.message ||
            res?.data?.data?.message ||
            (isEdit ? "Updated successfully" : "Company created successfully"),
        );

        setIsModalOpen(false);
        await fetchCompanies();
      } else {
        showErrorToast(
          res?.data?.message || res?.message || "Something went wrong",
        );
      }
    } catch (error) {
      showErrorToast(error?.response?.data?.message || "Server Error");
    } finally {
      setIsLoading(false);
      setImageUrls({});
    }
  };

  useEffect(() => {
    // if(isEdit) return

    let companyLogo;
    let companyQr;
    let directorSign;
    let favicon;
    if (form.company_logo instanceof File) {
      companyLogo = URL.createObjectURL(form.company_logo);

      console.log(form.company_logo instanceof File);
      console.log("file url", companyLogo);

      setImageUrls((prev) => ({ ...prev, company_logo: companyLogo }));
    }
    if (form.company_qr instanceof File) {
      companyQr = URL.createObjectURL(form.company_qr);
      setImageUrls((prev) => ({ ...prev, company_qr: companyQr }));
    }
    if (form.director_signature instanceof File) {
      directorSign = URL.createObjectURL(form.director_signature);
      setImageUrls((prev) => ({ ...prev, director_signature: directorSign }));
    }
    if (form.favicon instanceof File) {
      favicon = URL.createObjectURL(form.favicon);
      setImageUrls((prev) => ({ ...prev, favicon: favicon }));
    }

    console.log("Form", form);

    console.log("image Urls", imageUrls);

    return () => {
      URL.revokeObjectURL(companyLogo);
      URL.revokeObjectURL(companyQr);
      URL.revokeObjectURL(directorSign);
      URL.revokeObjectURL(favicon);
    };
  }, [
    isEdit,
    form?.company_logo,
    form.director_signature,
    form.company_qr,
    form.favicon,
  ]);

  const handleRemove = async (key) => {
    setImageUrls((prev) => ({ ...prev, [key]: "" }));
    setForm((prev) => ({ ...prev, [key]: "" }));
  };

  return (
    <div className="px-2 md:px-4  flex flex-col justify-between h-full">
      {isLoading && <Loader />}
      {companies?.length > 0 && (
        <div>
          <div
            className="w-full mb-2 px-5 py-2 rounded-2xl 
                bg-gradient-to-r from-cyan-700 via-blue-600 to-cyan-700
        text-white shadow-lg"
          >
            {/* Header Title */}
            <div className="flex items-center gap-3 text-2xl font-semibold tracking-wide mb-4">
              <Building2 className="w-6 h-6" />
              Company Details
            </div>

            {/* Tabs */}
            <div className="flex flex-wrap gap-3">
              {companies.map((item, index) => {
                const active = companyIndex === index;

                return (
                  <button
                    key={item.id}
                    onClick={() => setCompanyIndex(index)}
                    className={`px-5 py-2.5 rounded-xl text-sm font-medium transition-all duration-200
            ${
              active
                ? "bg-white text-indigo-600 shadow-md"
                : "bg-white/20 text-white hover:bg-white/30"
            }`}
                  >
                    {item.companyName.replace(/_/g, " ")}
                  </button>
                );
              })}
            </div>
          </div>

          <CompanyDetails companyData={companies[companyIndex]} />
        </div>
      )}

      <div className="pb-12">
        <div className="flex justify-between items-center gap-3 m-4">
          <h2 className="text-2xl md:text-3xl text-gray-900 font-semibold">
            Manage Company
          </h2>

          <Button className=" bg-blue-600 text-white" onClick={openCreateModal}>
            Add Company
          </Button>
        </div>

        {/* Table / Card list */}
        <div className="bg-white shadow-sm rounded-lg overflow-hidden">
          <div className=" md:block overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead
                className="bg-gradient-to-r from-cyan-700 via-blue-600 to-cyan-700
        text-white"
              >
                <tr>
                  <th className="px-4 py-4 text-left">Logo</th>
                  <th className="px-4 py-4 text-left">Name</th>
                  <th className="px-4 py-4 text-left">Email</th>
                  <th className="px-4 py-4 text-left">Contact</th>
                  <th className="px-4 py-4 text-left">Status</th>
                  <th className="px-4 py-4 text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {companies.map((c) => (
                  <tr key={c.id} className="border-t  hover:bg-gray-50">
                    <td className="flex items-end">
                      {c.companyLogo &&
                      c.companyLogo !== "https://example.com/logo.png" ? (
                        <span className="h-10 w-10 flex justify-center items-end">
                          <img
                            src={
                              process.env.REACT_APP_IMAGE_URL + c.companyLogo
                            }
                            alt={c.companyName}
                            className="h-8 w-8 bg-blue-900 rounded-full object-contain"
                          />
                        </span>
                      ) : (
                        <span className="bg-blue-50 rounded-full  flex justify-center items-end text-blue-700 p-2">
                          <Building2 />
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-2 font-medium">{c.companyName}</td>
                    <td className="px-4 py-2">{c.companyEmail}</td>
                    <td className="px-4 py-2">{c.companyContact}</td>
                    <td className="px-4 py-2">
                      {c.isActive ? (
                        <span className="inline-flex px-2 py-1 text-xs rounded-full bg-green-100 text-green-700">
                          Active
                        </span>
                      ) : (
                        <span className="inline-flex px-2 py-1 text-xs rounded-full bg-red-100 text-red-700">
                          Inactive
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-2 text-right">
                      <Button
                        size="sm"
                        variant="outline"
                        className="bg-amber-500"
                        onClick={() => openEditModal(c.id)}
                      >
                        Edit
                      </Button>
                    </td>
                  </tr>
                ))}

                {companies.length === 0 && (
                  <tr>
                    <td
                      colSpan={6}
                      className="px-4 py-6 text-center text-gray-500"
                    >
                      No companies found.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Mobile cards
          <div className="md:hidden divide-y">
            {companies.map((c) => (
              <div key={c.id} className="flex items-center justify-between p-3">
                <div className="flex items-center gap-3">
                  {c.companyLogo && (
                    <img
                      src={c.companyLogo}
                      alt={c.companyName}
                      className="h-10 w-10 rounded-full object-cover"
                    />
                  )}
                  <div>
                    <p className="font-semibold">{c.companyName}</p>
                    <p className="text-xs text-gray-500">{c.companyEmail}</p>
                  </div>
                </div>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => openEditModal(c.id)}
                >
                  Edit
                </Button>
              </div>
            ))}

            {companies.length === 0 && (
              <div className="p-4 text-center text-gray-500">
                No companies found.
              </div>
            )}
          </div> */}
        </div>
      </div>

      {/* Simple responsive modal */}
      {isModalOpen && (
        <div className="fixed inset-0 z-40 flex items-center justify-center bg-black/40">
          <div className="bg-white rounded-lg shadow-lg w-full max-w-7xl px-4 md:px-6 lg:px-8 py-4 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between px-2 py-1 border-b">
              <div className=" justify-between mb-2 w-full p-3 rounded-xl bg-gradient-to-r from-blue-50 to-blue-300 text-blue-700 flex items-center gap-3 font-bold">
                <span className="flex text-3xl  items-center gap-3">
                  {isEdit ? <FileEdit /> : <FilePlus2 />}
                  {isEdit ? "Edit Company" : "Create Company"}
                </span>

                <button
                  className="text-gray-500 hover:text-gray-700"
                  onClick={() => setIsModalOpen(false)}
                >
                  ✕
                </button>
              </div>
            </div>

            <form className="px-2 py-2 space-y-2" onSubmit={handleSubmit}>
              {/* 1st row */}
              <div className="grid grid-cols-2 md:grid-cols-4  gap-2">
                 {!isEdit &&
                 <div> <label className="block text-sm mb-1">
                    Company Name <span className="text-red-700">*</span>
                  </label>
                  <input
                    type="text"
                    name="company_name"
                    value={form.company_name}
                    onChange={handleChange}
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition"
                    required
                  />
                </div>
                 }

                <div>
                  <label className="block text-sm mb-1">Company Email</label>
                  <input
                    type="email"
                    name="company_email"
                    value={form.company_email}
                    onChange={handleChange}
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition"
                  />
                </div>

                <div>
                  <label className="block text-sm mb-1">Contact</label>
                  <input
                    type="text"
                    name="company_contact"
                    maxLength={10}
                    value={form.company_contact}
                    onChange={handleChange}
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition"
                  />
                </div>

                <div>
                  <label className="block text-sm mb-1">WhatsApp No</label>
                  <input
                    type="text"
                    maxLength={10}
                    name="company_whatsapp_no"
                    value={form.company_whatsapp_no}
                    onChange={handleChange}
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition"
                  />
                </div>

                <div>
                  <label className="block text-sm mb-1">CIN</label>
                  <input
                    type="text"
                    name="company_cin"
                    value={form.company_cin}
                    onChange={handleChange}
                    maxLength={21}
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition"
                  />
                </div>

                <div>
                  <label className="block text-sm mb-1">Company Capital</label>
                  <input
                    type="text"
                    name="company_capital"
                    value={form.company_capital}
                    onChange={handleChange}
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition"
                  />
                </div>

                <div>
                  <label className="block text-sm mb-1">
                    Company URL <span className="text-red-700">*</span>
                  </label>
                  <input
                    required
                    type="url"
                    name="company_url"
                    value={form.company_url}
                    onChange={handleChange}
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition"
                  />
                </div>
                <div>
                  <label className="block text-sm mb-1">Support URL</label>
                  <input
                    type="url"
                    name="support_url"
                    value={form.support_url}
                    onChange={handleChange}
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition"
                  />
                </div>

                <div>
                  <label className="block text-sm mb-1">Facebook URL</label>
                  <input
                    type="url"
                    name="facebook_url"
                    value={form.facebook_url}
                    onChange={handleChange}
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition"
                  />
                </div>
                <div>
                  <label className="block text-sm mb-1">Twitter URL</label>
                  <input
                    type="url"
                    name="twitter_url"
                    value={form.twitter_url}
                    onChange={handleChange}
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition"
                  />
                </div>

                <div>
                  <label className="block text-sm mb-1">Instagram URL</label>
                  <input
                    type="url"
                    name="instagram_url"
                    value={form.instagram_url}
                    onChange={handleChange}
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition"
                  />
                </div>
                <div>
                  <label className="block text-sm mb-1">YouTube URL</label>
                  <input
                    type="url"
                    name="youtube_url"
                    value={form.youtube_url}
                    onChange={handleChange}
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition"
                  />
                </div>

                <div>
                  <label className="block text-sm mb-1">
                    Patient App URL <span className="text-red-700">*</span>
                  </label>
                  <input
                    type="url"
                    required
                    name="patient_app"
                    value={form.patient_app}
                    onChange={handleChange}
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition"
                  />
                </div>
                <div>
                  <label className="block text-sm mb-1">
                    Employee App URL <span className="text-red-700">*</span>
                  </label>
                  <input
                    required
                    type="url"
                    name="employee_app"
                    value={form.employee_app}
                    onChange={handleChange}
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition"
                  />
                </div>
              </div>

              {/* Addresses */}
              <div>
                <label className="block text-sm mb-1">
                  Corporate Office Address{" "}
                  <span className="text-red-700">*</span>
                </label>
                <textarea
                  name="corporate_office_address"
                  value={form.corporate_office_address}
                  onChange={handleChange}
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition"
                  rows={2}
                />
              </div>
              <div>
                <label className="block text-sm mb-1">
                  Head Office Address <span className="text-red-700">*</span>
                </label>
                <textarea
                  name="head_office_address"
                  value={form.head_office_address}
                  onChange={handleChange}
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition"
                  rows={2}
                />
              </div>

              {/* Files */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
                {/* Upload Card */}
                {[
                  {
                    label: "Company Logo",
                    name: "company_logo",
                    required: true,
                  },
                  { label: "Company QR", name: "company_qr" },
                  { label: "Director Signature", name: "director_signature" },
                  { label: "Favicon", name: "favicon" },
                ].map((item) => (
                  <div key={item.name} className="flex flex-col">
                    <label className="text-sm font-semibold text-gray-700 mb-2">
                      {item.label}
                      {item.required && (
                        <span className="text-red-600 ml-1">*</span>
                      )}
                    </label>
                    {imageUrls[item.name] ? (
                      <div className="relative">
                        <img
                          src={
                            imageUrls[item.name].startsWith("blob")
                              ? imageUrls[item.name]
                              : process.env.REACT_APP_IMAGE_URL +
                                imageUrls[item.name]
                          }
                          className="w-full bg-blue-900 h-[150px] rounded-lg"
                          alt=""
                        />

                        <button
                          className="bg-rose-800 text-white p-2 rounded-full absolute top-2 right-2 flex items-center justify-center"
                          onClick={() => handleRemove(item.name)}
                        >
                          <Trash2 size={18} className=" " />
                        </button>
                      </div>
                    ) : (
                      <>
                        <label
                          className="
                      relative flex flex-col items-center justify-center
                      border-2 border-dashed border-blue-400
                      rounded-2xl
                      p-6
                      cursor-pointer
                      bg-blue-50
                      hover:bg-blue-100
                      transition-all duration-300
                      group
                    "
                        >
                          <span className="text-sm text-blue-700 font-medium">
                            Click to upload
                          </span>
                          <span className="text-xs text-gray-500 mt-1">
                            PNG, JPG up to 5MB
                          </span>

                          <input
                            type="file"
                            name={item.name}
                            accept="image/*"
                            // required={item.required}
                            onChange={handleFileChange}
                            className="absolute inset-0 opacity-0 cursor-pointer"
                          />
                        </label>
                      </>
                    )}
                  </div>
                ))}
              </div>

              <div>
                <label className="block text-sm mb-1">Copyright Name</label>
                <input
                  type="text"
                  name="copyright_name"
                  value={form.copyright_name}
                  onChange={handleChange}
                  className="w-full border rounded px-2 py-1 text-sm border-blue-400"
                />
              </div>

              <div className="flex items-center justify-end gap-2 pt-2 border-t mt-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setIsModalOpen(false)}
                >
                  Cancel
                </Button>
                <Button type="submit" className="bg-blue-500 text-white">
                  {isEdit ? "Update" : "Create"}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default ManageCompany;
